// Measures whether the departure city we propose is the one the visitor wanted.
//
// The departure field is prefilled from an IP lookup, and an IP lookup is a guess:
// carrier and relay addresses resolve to a different city in every database. Until
// now nothing recorded how often that guess was corrected, so there was no way to
// tell a prefill that saves a visitor a step from one that hands them a form to
// undo. Two events answer it: what we proposed, and what the visitor replaced it
// with — each carrying the confidence and network signals the proposal came from.

import axios from '@/axios';
import { getVisitorId } from '@/utils/visitorId';

const STORAGE_KEY = 'pfOriginAutofill';

// Per tab, not per browser: the question is "did this visit keep the proposal",
// and a stale flag from last week would silently drop today's correction.
const readProposal = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
  } catch (_) {
    return null;
  }
};

const writeProposal = (proposal) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(proposal));
  } catch (_) {
    /* storage blocked — the events simply stop being paired */
  }
};

const send = (eventType, destination, extra) => {
  axios.post('/analytics/track-event/', {
    event_type: eventType,
    destination,
    extra,
    visitor_id: getVisitorId(),
  }).catch(() => {});
};

// Every signal that travels with a proposal, and must travel with its correction too:
// a breakdown groups both events on the same key, so a field recorded on only one side
// puts all of that group's corrections in the 'unknown' bucket and reads as a zero
// change rate. Kept as one list because two hand-maintained copies already drifted.
const SIGNAL_KEYS = [
  'confidence',
  'accuracy',
  'network_type',
  'provider',
  'agreeing',
  'sources',
  'hub_swap',
  'hub_swap_from',
  'hub_swap_km',
];

const replaySignals = (proposal) => SIGNAL_KEYS.reduce((carried, key) => {
  carried[key] = proposal[key] ?? null;
  return carried;
}, {});

const signalsOf = (location) => {
  const signals = location?.signals || {};
  const hubSwap = signals.hub_swap || null;
  return {
    confidence: location?.confidence || 'unknown',
    accuracy: location?.accuracy || 'unknown',
    network_type: signals.network_type || 'unknown',
    provider: signals.provider || 'unknown',
    agreeing: signals.agreeing ?? null,
    sources: signals.sources ?? null,
    // Sent as a string, not a boolean: the report groups on `value || 'unknown'`,
    // which would quietly fold every `false` into the unknown bucket.
    hub_swap: hubSwap ? 'yes' : 'no',
    // What the visitor's own nearest airport was, so a correction back to it is
    // visible as "the diversion was unwanted" rather than an unexplained change.
    hub_swap_from: hubSwap?.from || null,
    hub_swap_km: hubSwap?.distance_km ?? null,
  };
};

/**
 * Record that a detected city was written into a departure field.
 *
 * @param {object} location  The resolved location the prefill came from.
 * @param {string} surface   Which form did it ('search-form', 'departure-field', …).
 */
export const trackOriginAutofill = (location, surface) => {
  const code = location?.city?.code;
  if (!code) return;

  // The same city proposed again on the next page is the same proposal: its
  // record is left untouched, so a reload neither counts a second proposal nor
  // re-arms the correction it already recorded. Later loads also read the
  // location from cache, without the backend signals the first one carried.
  if (readProposal()?.code === code) return;

  writeProposal({
    code,
    surface,
    at: Date.now(),
    ...signalsOf(location),
  });
  send('origin_autofill', code, { ...signalsOf(location), surface });
};

/**
 * Record that the visitor replaced or cleared the city we proposed.
 *
 * @param {string} newCode  The city the visitor chose, or '' when they cleared it.
 * @param {string} surface  Where the correction happened.
 */
export const trackOriginChanged = (newCode, surface) => {
  const proposal = readProposal();
  if (!proposal?.code || proposal.changed) return;
  // Re-picking the proposed city is not a correction.
  if (newCode && String(newCode).toUpperCase() === String(proposal.code).toUpperCase()) return;

  writeProposal({ ...proposal, changed: true });
  send('origin_changed', String(newCode || '').toUpperCase(), {
    ...replaySignals(proposal),
    from_code: proposal.code,
    surface,
    action: newCode ? 'replaced' : 'cleared',
    seconds_since_autofill: Math.round((Date.now() - (proposal.at || Date.now())) / 1000),
  });
};

/** The city currently proposed to this visitor, if any. */
export const proposedOriginCode = () => readProposal()?.code || null;
